/**
 * 付费计划组件
 *
 * 展示LV0-LV3四阶会员计划
 * 横向四列网格布局
 *
 * 点击订阅按钮：
 * - 已登录 → 跳转作者中心充值页 /recharge
 * - 未登录 → 跳转登录页，登录后自动回到 /recharge（由 ProtectedRoute 的 state.from 实现）
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { homePageContent } from './homePageContent';
import { useAuthStore } from '../../store/authStore';

interface PricingCardProps {
  level: string;
  name: string;
  price: string;
  features: string[];
  featured?: boolean;
  recommended?: boolean;
  onClick?: () => void;
}

const PricingCard: React.FC<PricingCardProps> = ({
  level,
  name,
  price,
  features,
  featured,
  recommended,
  onClick,
}) => (
  <div className={`relative rounded-2xl p-6 transition-all duration-150 ${
    featured
      ? 'bg-white border-2 border-paper-400 shadow-sm'
      : 'bg-white border border-gray-200 shadow-xs hover:shadow-sm'
  }`}>
    {/* 推荐标签 */}
    {recommended && (
      <div className="absolute -top-3 left-1/2 -translate-x-1/2">
        <span className="px-4 py-1 bg-gradient-to-r from-ink-800 to-ink-700 text-white text-sm font-medium rounded-full shadow-md">
          最受欢迎
        </span>
      </div>
    )}

    {/* 等级标识 */}
    <div className="flex items-center justify-between mb-4">
      <div>
        <span className={`text-sm font-bold ${featured ? 'text-ink-800' : 'text-ink-500'}`}>
          {level}
        </span>
        <h3 className="text-xl font-bold text-ink-900">{name}</h3>
      </div>
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
        featured ? 'bg-gradient-to-br from-ink-800 to-ink-700' : 'bg-paper-100'
      }`}>
        <span className="text-xl">{featured ? '👑' : level === 'LV0' ? '📝' : level === 'LV1' ? '🎯' : '🌟'}</span>
      </div>
    </div>

    {/* 价格 */}
    <div className="mb-6">
      <span className="text-3xl font-bold text-ink-900">{price}</span>
      {price !== 'Free' && <span className="text-ink-500">/月</span>}
    </div>

    {/* 功能列表 */}
    <ul className="space-y-3 mb-8">
      {features.map((feature, index) => (
        <li key={index} className="flex items-start gap-3 text-ink-700">
          <svg className={`w-5 h-5 flex-shrink-0 mt-0.5 ${featured ? 'text-paper-700' : 'text-ink-400'}`} fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
          <span className="text-sm">{feature}</span>
        </li>
      ))}
    </ul>

    {/* CTA按钮 */}
    <button
      onClick={onClick}
      className={`w-full py-3 rounded-xl font-medium transition-all duration-150 ${
        featured
          ? 'bg-ink-800 text-white shadow-sm hover:shadow-md hover:opacity-90'
          : 'bg-paper-100 text-ink-700 hover:bg-paper-200'
      }`}>
      {price === 'Free' ? '免费开始' : '立即订阅'}
    </button>
  </div>
);

export const PricingSection: React.FC = () => {
  const { pricing } = homePageContent;
  const navigate = useNavigate();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  const handleSubscribe = () => {
    if (isAuthenticated) {
      navigate('/recharge');
    } else {
      // 未登录 → 登录页，登录后由 LoginPage 读取 state.from 自动跳转 /recharge
      navigate('/login', { state: { from: { pathname: '/recharge' } } });
    }
  };

  return (
    <section className="py-24 px-4 md:px-8 bg-white relative">

      <div className="max-w-7xl mx-auto">
        {/* 标题区域 */}
        <div className="text-center mb-16">
        <span className="inline-block px-4 py-1.5 rounded-full bg-paper-200 text-paper-800 text-sm font-medium mb-4">
          {pricing.badge}
        </span>
        <h2 className="text-4xl md:text-5xl font-bold text-ink-900 mb-4">
          {pricing.title}
        </h2>
        <p className="text-xl text-ink-600 max-w-2xl mx-auto">
            {pricing.subtitle}
          </p>
        </div>

        {/* 价格卡片网格 */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {pricing.plans.map((plan, index) => (
            <PricingCard
              key={index}
              level={plan.level}
              name={plan.name}
              price={plan.price}
              features={plan.features}
              featured={plan.featured}
              recommended={plan.recommended}
              onClick={handleSubscribe}
            />
          ))}
        </div>

        {/* 底部说明 */}
        <p className="text-center text-ink-500 text-sm mt-12">
          {pricing.footer}
        </p>
      </div>
    </section>
  );
};
